import { TapTracerBars } from './LaborCharts';
import { validatePayload } from './validate';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ClinicalSubject } from './types';
import type { ProposedEntry } from './PrenatalEntry';
export interface TracerSample {
  offset_ms: number;
  bpm: number;
  contraction: boolean;
}
export function summarizeTracer(samples: TracerSample[]) {
  if (
    !samples.length ||
    samples.some(
      (s, i) =>
        !Number.isFinite(s.bpm) ||
        s.bpm <= 0 ||
        s.bpm > 300 ||
        !Number.isFinite(s.offset_ms) ||
        s.offset_ms <= (samples[i - 1]?.offset_ms ?? 0),
    )
  )
    return null;
  const bpms = samples.map((s) => s.bpm).sort((a, b) => a - b);
  return {
    low: bpms[0],
    high: bpms.at(-1)!,
    median:
      bpms.length % 2 ? bpms[Math.floor(bpms.length / 2)] : (bpms[bpms.length / 2 - 1] + bpms[bpms.length / 2]) / 2,
  };
}
export function contractionSummary(intervals: { start: number; end: number }[]) {
  if (!intervals.length || intervals.some((i) => i.end <= i.start)) return null;
  const durations = intervals.map((i) => (i.end - i.start) / 1000);
  const frequencies = intervals.slice(1).map((i, index) => (i.start - intervals[index].start) / 60000);
  return {
    duration_s_low: Math.min(...durations),
    duration_s_high: Math.max(...durations),
    ...(frequencies.length ? { freq_min_low: Math.min(...frequencies), freq_min_high: Math.max(...frequencies) } : {}),
  };
}
export function LaborTimedTools({
  mode,
  subjects,
  disabled,
  onSave,
}: {
  mode: 'FHR' | 'CONTRACTIONS';
  subjects: ClinicalSubject[];
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
}) {
  const allowed = subjects.filter(
    (s) => s.active !== false && s.subject_type === (mode === 'FHR' ? 'FETUS' : 'MOTHER'),
  );
  const [subjectId, setSubjectId] = useState(allowed.length === 1 ? allowed[0].id : '');
  const [running, setRunning] = useState(false),
    [samples, setSamples] = useState<TracerSample[]>([]),
    [intervals, setIntervals] = useState<{ start: number; end: number }[]>([]),
    [message, setMessage] = useState(''),
    [reviewed, setReviewed] = useState(false),
    [frequency, setFrequency] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const ctxInWindow = useRef(false);
  const sampleCount = useRef(0);
  const [inContraction, setInContraction] = useState(false);
  const ctx = useRef(false),
    beats = useRef(0),
    start = useRef(0),
    bucket = useRef(0),
    ctxStart = useRef<number | null>(null),
    observed = useRef('');
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const now = performance.now();
      if (mode === 'FHR' && now - bucket.current >= 5000) {
        const elapsed = now - bucket.current;
        if (elapsed > 6000 || sampleCount.current >= 720) {
          setRunning(false);
          setMessage(
            elapsed > 6000
              ? 'Timing paused or the device was suspended. The incomplete window was discarded; review retained samples.'
              : 'Recording limit reached. Review this tracing before starting another.',
          );
          return;
        }
        sampleCount.current++;
        // Snapshot refs before resetting; React may defer the state updater.
        const completed = {
          offset_ms: now - start.current,
          bpm: Math.round((beats.current / elapsed) * 60000),
          contraction: ctxInWindow.current,
        };
        setSamples((old) => [...old, completed]);
        ctxInWindow.current = ctx.current;
        beats.current = 0;
        bucket.current = now;
      }
    }, 100);
    const hidden = () => {
      if (document.hidden) {
        setRunning(false);
        setMessage(
          'Timing stopped when the page was hidden. Review the recorded intervals; unattended time is not treated as an observation.',
        );
      }
    };
    document.addEventListener('visibilitychange', hidden);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [running, mode]);
  const summary = summarizeTracer(samples);
  const contraction = contractionSummary(intervals);
  function begin() {
    if (!allowed.some((s) => s.id === subjectId)) {
      setMessage('Choose a subject.');
      return;
    }
    ctxStart.current = null;
    ctx.current = false;
    ctxInWindow.current = false;
    sampleCount.current = 0;
    setInContraction(false);
    observed.current = new Date().toISOString();
    start.current = performance.now();
    bucket.current = start.current;
    beats.current = 0;
    setSamples([]);
    setIntervals([]);
    setReviewed(false);
    setMessage('');
    setRunning(true);
  }
  async function save() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!reviewed || running || disabled) throw new Error('Stop the timer and review its observations first.');
      let payload: Record<string, unknown>;
      if (mode === 'FHR') {
        if (!summary)
          throw new Error('Every retained 5-second sample must contain observed beats. Repeat an incomplete tracing.');
        payload = {
          low_bpm: summary.low,
          high_bpm: summary.high,
          listen_duration_s: samples.at(-1)!.offset_ms / 1000,
          device: 'Manual 5-second tap count',
          tracer_samples: samples,
        };
      } else {
        if (!contraction) throw new Error('Record a complete contraction.');
        const freq = contraction.freq_min_low ?? Number(frequency);
        if (!Number.isFinite(freq) || freq <= 0)
          throw new Error('Record two contractions or enter a reviewed frequency.');
        payload = { ...contraction, freq_min_low: freq, palpated_or_reported: 'Timed by clinician' };
      }
      if (!allowed.some((s) => s.id === subjectId)) throw new Error('The selected subject is no longer available.');
      const validation = validatePayload(mode === 'FHR' ? 'LABOR.FHR' : 'LABOR.CONTRACTIONS', payload, 2);
      if (validation.length) throw new Error(validation.join('; '));
      await onSave(
        [
          {
            event_type: mode === 'FHR' ? 'LABOR.FHR' : 'LABOR.CONTRACTIONS',
            schema_version: 2,
            subject_id: subjectId,
            payload,
          },
        ],
        observed.current,
      );
      setSamples([]);
      setIntervals([]);
      setReviewed(false);
      setMessage('Saved locally.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Unable to save.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  return (
    <FieldSet disabled={disabled || saving}>
      <FieldLegend>{mode === 'FHR' ? 'Five-second FHR tap tracer' : 'Contraction timer'}</FieldLegend>
      <p className="text-sm text-muted-foreground">
        Timing aid for attended observation. Review findings before saving; the app cannot monitor while the browser is
        suspended.
      </p>
      {allowed.length > 1 && (
        <Field>
          <FieldLabel>Subject</FieldLabel>
          <Select
            value={subjectId}
            onValueChange={setSubjectId}
            disabled={running || samples.length > 0 || intervals.length > 0}>
            <SelectTrigger className="min-h-11">
              <SelectValue placeholder="Choose subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {allowed.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      )}
      <div className="flex flex-wrap gap-3">
        <Button type="button" className="min-h-11" disabled={running || saving} onClick={begin}>
          Start attended timing
        </Button>
        <Button
          type="button"
          className="min-h-11"
          variant="outline"
          disabled={!running}
          onClick={() => {
            if (ctxStart.current !== null) {
              setMessage('End the current contraction before stopping.');
              return;
            }
            setRunning(false);
          }}>
          Stop timing
        </Button>
        {mode === 'FHR' ? (
          <>
            <Button
              type="button"
              className="min-h-16 min-w-40"
              disabled={!running}
              onClick={() => {
                beats.current++;
              }}>
              Tap each beat
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={!running}
              onClick={() => {
                ctx.current = !ctx.current;
                ctxInWindow.current ||= ctx.current;
                setInContraction(ctx.current);
              }}>
              {inContraction ? 'End contraction marker' : 'Mark contraction'}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            className="min-h-16"
            disabled={!running}
            onClick={() => {
              const now = performance.now();
              if (ctxStart.current === null) {
                ctxStart.current = now;
                setInContraction(true);
              } else {
                const value = { start: ctxStart.current, end: now };
                setIntervals((old) => [...old, value]);
                ctxStart.current = null;
                setInContraction(false);
              }
            }}>
            {inContraction ? 'Contraction ends' : 'Contraction starts'}
          </Button>
        )}
      </div>
      {mode === 'FHR' ? (
        <div className="flex flex-col gap-2">
          <p>
            {samples.length} completed five-second samples
            {summary ? ` · low ${summary.low}, high ${summary.high}, median ${summary.median} bpm` : ''}. Mean{' '}
            {summary
              ? (
                  samples.reduce((sum, s, i) => sum + s.bpm * (s.offset_ms - (samples[i - 1]?.offset_ms ?? 0)), 0) /
                  samples.at(-1)!.offset_ms
                ).toFixed(1)
              : '—'}{' '}
            bpm. Neither mean nor median is automatically a baseline assessment. An unfinished final window is omitted.
          </p>
          <TapTracerBars
            samples={samples.map((s, i) => ({ ...s, start: samples[i - 1]?.offset_ms ?? 0, end: s.offset_ms }))}
          />
        </div>
      ) : (
        <>
          <p>
            {intervals.length} completed contractions
            {contraction
              ? ` · duration ${contraction.duration_s_low.toFixed(1)}–${contraction.duration_s_high.toFixed(1)} seconds`
              : ''}
            .
          </p>
          {intervals.length === 1 && (
            <Field>
              <FieldLabel htmlFor="timed-frequency">Reviewed frequency (minutes)</FieldLabel>
              <Input
                id="timed-frequency"
                className="min-h-11"
                type="number"
                step="any"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              />
            </Field>
          )}
        </>
      )}
      <Field orientation="horizontal">
        <Checkbox
          id={`timed-review-${mode}`}
          checked={reviewed}
          disabled={running}
          onCheckedChange={(v) => setReviewed(v === true)}
        />
        <FieldLabel htmlFor={`timed-review-${mode}`}>
          I reviewed the timed observations and selected subject.
        </FieldLabel>
      </Field>
      <Button type="button" className="min-h-11" disabled={!reviewed || running} onClick={save}>
        Save reviewed timed observations
      </Button>
      {message && <p role="status">{message}</p>}
    </FieldSet>
  );
}
